import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import _ from 'lodash';
import PubSub from 'pubsub-js';
import { Link, useLocation } from 'react-router-dom';
import RouterHelper from '../../../core/RouterHelper';
import UserManager from '../../../core/UserManager';
import Button from 'components/markup/Button';
import { IconContext } from 'react-icons';
import { useSelector } from 'react-redux';
import { CFMenuIcon } from './img/Icons';
import Tooltip from '../../markup/Tooltip';
import PlatformSwitcher from './PlatformSwitcher';
import { useClickOutside } from '../../../utils/func';
import GlobalConst from '../../../utils/GlobalConst';
import { selectAuthUser } from '../../../store/authSlice';
import { getFilteredNavForPlatform, getIconMapForPlatform, getPlatformTheme, getMenuBottomForPlatform, openExternalWithReferralToken } from './MenuDataProvider';
import { MdSearch, MdClose } from 'react-icons/md';
import './styles.less';
/* global __APP_PLATFORM__ */

const isSelectedPath = (openedPage, path) => (
	openedPage === path || openedPage.startsWith(path + '/')
);

const checkImpersonating = () => {
	const originalAuthToken = localStorage.getItem('auth_token_original_user');
	const currentAuthToken = localStorage.getItem('auth_token');
	return !_.isEmpty(originalAuthToken) && originalAuthToken !== currentAuthToken;
};

const Menu = ({ children, exportsStatus, onFetchExportsStatus }) => {
	const location = useLocation();
	const openedPage = location.pathname;
	const [collapsedParents, setCollapsedParents] = useState(() => new Set());
	const [menuExpanded, setMenuExpanded] = useState(false);
	const [impersonating, setImpersonating] = useState(checkImpersonating);
	const [searchQuery, setSearchQuery] = useState('');
	const authUser = useSelector(selectAuthUser) || {};
	const menuRef = useRef(null);
	const navItemsContainerRef = useRef(null);
	const searchInputRef = useRef(null);
	const wasMenuExpandedRef = useRef(menuExpanded);
	const menuBottomRef = useRef(null);
	const hoverTimeoutRef = useRef(null);
	const HOVER_DELAY_MS = 100; // Added this to delay in case there is an accidental mouse hover over the menu

	const currentPlatform = useMemo(() => __APP_PLATFORM__, []);

	const iconMap = useMemo(() => getIconMapForPlatform(currentPlatform), [currentPlatform]);
	const getIcon = useCallback((iconName) => iconMap[iconName] || null, [iconMap]);
	const theme = useMemo(() => getPlatformTheme(currentPlatform), [currentPlatform]);
	const MenuBottomComponent = useMemo(() => getMenuBottomForPlatform(currentPlatform), [currentPlatform]);

	const isDarkTheme = authUser?.uiTheme === 'dark';

	const platformCssVars = useMemo(() => {
		if (isDarkTheme) {
			return {
				'--platform-accent': 'var(--action-primary)',
				'--platform-hover-bg': 'var(--surface-hover)',
				'--platform-selected-bg': 'var(--action-primary-bg)'
			};
		}
		return {
			'--platform-accent': theme.accent,
			'--platform-hover-bg': theme.hoverBg,
			'--platform-selected-bg': theme.selectedBg
		};
	}, [theme, isDarkTheme]);

	const filteredNav = useMemo(() => {
		return getFilteredNavForPlatform({
			platformId: currentPlatform,
			perms: authUser?.perms || {},
			authUser
		});
	}, [currentPlatform, authUser]);

	const allNavItems = useMemo(() => {
		const { sections = [], pinnedItems = [] } = filteredNav;
		const items = [];
		sections.forEach((section) => {
			(section.items || []).forEach((item) => {
				items.push({ ...item, sectionIcon: section.icon, sectionLabel: section.label });
			});
		});
		pinnedItems.forEach((item) => {
			items.push({ ...item, sectionIcon: item.icon, sectionLabel: null });
		});
		return items;
	}, [filteredNav]);

	const searchResults = useMemo(() => {
		if (!searchQuery.trim()) return [];
		const query = searchQuery.toLowerCase().trim();
		return allNavItems.filter((item) => item.label.toLowerCase().includes(query));
	}, [searchQuery, allNavItems]);

	useClickOutside(
		menuRef,
		() => {
			if (collapsedParents.size > 0 || menuExpanded) {
				setCollapsedParents(new Set());
				setMenuExpanded(false);
			}
			if (menuBottomRef.current?.closePopover) {
				menuBottomRef.current.closePopover();
			}
		},
		[collapsedParents, menuExpanded]
	);

	useEffect(() => {
		const handleUserChanged = () => setImpersonating(checkImpersonating());
		PubSub.subscribe(GlobalConst.USER_CHANGED_MSG, handleUserChanged);
		return () => {
			PubSub.unsubscribe(handleUserChanged);
		};
	}, []);

	useEffect(() => {
		if (wasMenuExpandedRef.current && !menuExpanded) {
			navItemsContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
			setSearchQuery('');
		}
		wasMenuExpandedRef.current = menuExpanded;
	}, [menuExpanded]);

	const scrollToTop = useCallback(() => {
		(document.getElementById('main-page-wrapper') || window).scrollTo({ top: 0, behavior: 'smooth' });
	}, []);

	const onPathClick = useCallback(
		(path, title = null, disabled = false, externalLink = null, tokenType = null) => {
			if (disabled) return;
			if (tokenType) {
				openExternalWithReferralToken(tokenType, externalLink);
				return;
			}
			if (externalLink) {
				RouterHelper.goToExternal(externalLink);
				return;
			}
			scrollToTop();
			if (title) RouterHelper.setTitle(title);
			if (window.location.pathname.includes('/exports_manager') && onFetchExportsStatus) {
				onFetchExportsStatus();
			}
		},
		[scrollToTop, onFetchExportsStatus]
	);

	const onClickParentItem = useCallback((path) => {
		setCollapsedParents((prev) => {
			const next = new Set(prev);
			if (next.has(path)) {
				next.delete(path);
			} else {
				next.add(path);
			}
			return next;
		});
	}, []);

	const clearHoverTimeout = useCallback(() => {
		if (hoverTimeoutRef.current) {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = null;
		}
	}, []);

	const handleMenuMouseEnter = useCallback(() => {
		clearHoverTimeout();
		if (menuExpanded) return;
		hoverTimeoutRef.current = setTimeout(() => {
			setMenuExpanded(true);
		}, HOVER_DELAY_MS);
	}, [menuExpanded, clearHoverTimeout]);

	const handleMenuMouseLeave = useCallback(() => {
		clearHoverTimeout();
		hoverTimeoutRef.current = setTimeout(() => {
			setMenuExpanded(false);
			setCollapsedParents(new Set());
			setSearchQuery('');
			if (menuBottomRef.current?.closePopover) {
				menuBottomRef.current.closePopover();
			}
		}, HOVER_DELAY_MS);
	}, [clearHoverTimeout]);

	useEffect(() => {
		return () => clearHoverTimeout();
	}, [clearHoverTimeout]);

	const renderSectionSeparator = useCallback((sectionName = null, icon = null) => {
		return (
			<div className="section-separator">
				{icon && (
					<IconContext.Provider value={{ style: { fontSize: '16px', color: '#706F6F' } }}>
						{icon}
					</IconContext.Provider>
				)}
				{sectionName && <div className="section-name">{sectionName}</div>}
				<div className="separator-line" />
			</div>
		);
	}, []);

	const renderImpersonationBar = useCallback(() => {
		const { firstName, lastName } = authUser;
		return (
			<div className="impersonation-bar">
				<div className="bar-text">
					You are logged in as
					<br />
					{firstName} {lastName}
				</div>
				<Button className="btn return-btn" onClick={() => UserManager.loginAsOriginalUser()}>
					Back to my account
				</Button>
			</div>
		);
	}, [authUser]);

	const renderExportStatus = useCallback(() => {
		if (!exportsStatus) return null;
		if (exportsStatus.Complete > 0) {
			return (
				<div className="exports-status">
					<div className="completed">{exportsStatus.Complete > 99 ? 99 : exportsStatus.Complete}</div>
				</div>
			);
		}
		if (!exportsStatus.Complete && exportsStatus.Processing > 0) {
			return (
				<div className="exports-status">
					<div className="processing">
						<i className="fa fa-refresh rotating-i" />
					</div>
				</div>
			);
		}
		return null;
	}, [exportsStatus]);

	const renderNavLabel = useCallback((label) => {
		if (!label) return null;
		const key = label.toLowerCase();
		return <span className={cx('nav-label', key)}>{label}</span>;
	}, []);

	const renderSinglePathItem = useCallback(
		(path, title, isVisible = true, icon = null, extra = null, label = null, disabled = false, externalLink = null) => {
			if (!isVisible) return null;

			const selectedItem = isSelectedPath(openedPage, path);
			const finalPath = externalLink ? openedPage : path;
			return (
				<Link key={path} data-testid={finalPath} to={finalPath} className={cx('single-item', { selected: selectedItem, disabled: disabled })} onClick={() => onPathClick(finalPath, title, disabled, externalLink)}>
					<Tooltip className="d-flex" tip={!menuExpanded ? title : ''}>
						{icon && (
							<IconContext.Provider
								value={{
									style: {
										fontSize: '20px',
										color: theme.accent
									}
								}}
							>
								{icon}
							</IconContext.Provider>
						)}
						<div className="expanded-area ml-10">{title}</div>
						{extra}
						{renderNavLabel(label)}
					</Tooltip>
				</Link>
			);
		},
		[openedPage, menuExpanded, onPathClick, theme, renderNavLabel]
	);

	const renderParentItem = useCallback(
		(path, title, childrenNodes, isVisible = true, icon = null, childPaths = []) => {
			if (!isVisible) return null;

			const selectedItem = childPaths.some((cp) => isSelectedPath(openedPage, cp));
			const parentMenuOpened = menuExpanded && !collapsedParents.has(path);

			return (
				<div key={path} className={cx('has-children', { hidden: !isVisible })}>
					<span className={cx('parent-layer-menu', { opened: parentMenuOpened, selected: selectedItem && !menuExpanded })} data-testid={path} onClick={() => onClickParentItem(path)}>
						<Tooltip className="d-flex" tip={!menuExpanded ? title : ''}>
							{icon && (
								<IconContext.Provider
									value={{
										style: {
											fontSize: '20px',
											color: theme.accent
										}
									}}
								>
									{icon}
								</IconContext.Provider>
							)}
							<div className="expanded-area ml-10">
								{title}
							</div>
						</Tooltip>
					</span>
					<div id={path} className={cx('child-menu', { visible: parentMenuOpened })}>
						{childrenNodes}
					</div>
				</div>
			);
		},
		[openedPage, collapsedParents, menuExpanded, onClickParentItem, theme]
	);

	const renderSearchResults = useCallback(() => {
		if (!searchResults.length) {
			return <div className="search-no-results">No results found</div>;
		}
		return searchResults.map((item) => {
			const selectedItem = isSelectedPath(openedPage, item.path);
			const finalPath = item.externalLink ? openedPage : item.path;
			return (
				<div key={item.path} className={cx('child-item', { selected: selectedItem })}>
					<Link to={finalPath} className={cx('child-item-link', { disabled: item.disabled })} onClick={() => { onPathClick(finalPath, item.label, item.disabled, item.externalLink); setSearchQuery(''); }}>
						{item.label}
					</Link>
					{renderNavLabel(item.navLabel || null)}
				</div>
			);
		});
	}, [searchResults, openedPage, onPathClick, renderNavLabel]);

	const renderSubmenuChildItem = useCallback(
		(path, title, isVisible = true, extra = null, label = null, disabled = false, externalLink = null, tokenType = null) => {
			if (!isVisible) return null;

			const selectedItem = isSelectedPath(openedPage, path);
			const handlesClickInternally = !!(tokenType || externalLink);
			const finalPath = handlesClickInternally ? openedPage : path;
			const handleClick = (e) => {
				if (handlesClickInternally) e.preventDefault();
				onPathClick(path, title, disabled, externalLink, tokenType);
			};
			return (
				<div key={title} className={cx('child-item', { selected: selectedItem })}>
					<Link data-testid={finalPath} to={finalPath} className={cx('child-item-link', { disabled: disabled })} onClick={handleClick}>
						{title}
					</Link>
					{extra}
					{renderNavLabel(label)}
				</div>
			);
		},
		[onPathClick, openedPage, renderNavLabel]
	);

	const renderSectionFromConfig = useCallback(
		(section) => {
			if (!section?.items?.length) return null;

			const sectionBasePath = `/${section.id}/`;
			const sectionIcon = getIcon(section.icon);

			if (section.singleLink) {
				const item = section.items[0];
				return renderSinglePathItem(item.path, item.label, true, sectionIcon, null, item.navLabel || null);
			}

			return renderParentItem(
				sectionBasePath,
				section.label,
				section.items.map((item) => renderSubmenuChildItem(
					item.path,
					item.label,
					true,
					item.id === 'exports' ? renderExportStatus() : null,
					item.navLabel || null,
					item.disabled || false,
					item.externalLink || null,
					item.tokenType || null
				)),
				true,
				sectionIcon,
				section.items.map((item) => item.path)
			);
		},
		[renderParentItem, renderSubmenuChildItem, renderSinglePathItem, renderExportStatus]
	);

	const renderNavItems = useCallback(() => {
		const { sections = [], pinnedItems = [] } = filteredNav;
		return (
			<div className="navitems-container" ref={navItemsContainerRef}>
				<div className="section-items">
					{sections.map((section) => renderSectionFromConfig(section))}
				</div>

				{pinnedItems.map((item) => (
					<React.Fragment key={item.id}>
						{renderSectionSeparator()}
						<div className="section-items">
							{renderSinglePathItem(item.path, item.label, true, getIcon(item.icon))}
						</div>
					</React.Fragment>
				))}

				{impersonating && renderImpersonationBar()}
			</div>
		);
	}, [
		filteredNav,
		renderSectionSeparator,
		renderSinglePathItem,
		renderSectionFromConfig,
		getIcon,
		impersonating,
		renderImpersonationBar
	]);

	return (
		<div style={{ display: 'flex' }}>
			<div className={cx('side-main-menu', { closed: !menuExpanded })} id="side-main-menu" ref={menuRef} style={platformCssVars} onMouseEnter={handleMenuMouseEnter} onMouseLeave={handleMenuMouseLeave}>
				<div className="menu-header">
					<div className="main-logo-container">
						<div className="menu-icon"><CFMenuIcon /></div>
						{menuExpanded && <PlatformSwitcher menuExpanded={menuExpanded} onPathClick={onPathClick} authUser={authUser} impersonating={impersonating} />}
					</div>
					{menuExpanded && (
						<div className="menu-search">
							<MdSearch className="menu-search-icon" />
							<input
								ref={searchInputRef}
								type="text"
								className="menu-search-input"
								placeholder="Search features..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
							/>
							{searchQuery && <MdClose className="menu-search-clear" onClick={() => { setSearchQuery(''); searchInputRef.current?.focus(); }} />}
						</div>
					)}
				</div>
				{searchQuery.trim() ? (
					<div className="navitems-container" ref={navItemsContainerRef}>
						<div className="section-items">
							<div className="has-children">
								<div className="child-menu visible">
									{renderSearchResults()}
								</div>
							</div>
						</div>
					</div>
				) : renderNavItems()}
				{MenuBottomComponent && <MenuBottomComponent ref={menuBottomRef} menuExpanded={menuExpanded} onExpand={() => setMenuExpanded(true)} />}
			</div>
			<div id='main-page-wrapper' style={{ minWidth: '1150px', flex: '1', overflowY: 'scroll', height: '100vh' }}>{children}</div>
		</div>
	);
};

Menu.propTypes = {
	children: PropTypes.node,
	exportsStatus: PropTypes.object,
	onFetchExportsStatus: PropTypes.func
};

export default Menu;
